import type { ArrayBufferType } from "./marshalTypedArrayTypes";
import {
  IS_BIGINT_ARRAY,
  LEGACY_TYPED_ARRAY_LABEL,
  TYPED_ARRAY_LABEL,
  TYPE_TO_ARRAY_CONSTRUCTOR,
} from "./marshalTypedArrayTypes";

/**
 * Replaces objects transformed for storage in JSON that should be typed arrays back into
 * real typed arrays
 */
export function unmarshalTypedArraysInObject(object: any): any {
  if (object === null || object === undefined) {
    return object;
  }

  if (typeof object === "object") {
    // This makes sure that we don't unmarshal typed arrays that are already unmarshalled
    if (ArrayBuffer.isView(object)) {
      return object;
    }

    const arrayBufferView = unmarshalTypedArray(object);
    if (arrayBufferView) {
      return arrayBufferView;
    }

    if (Array.isArray(object)) {
      return object.map(unmarshalTypedArraysInObject);
    }

    const dst: { [key: string]: any } = {};
    for (const key in object) {
      if (object.hasOwnProperty(key)) {
        dst[key] = unmarshalTypedArraysInObject(object[key]);
      }
    }

    return dst;
  }

  return object;
}

export function unmarshalTypedArray(object: any): ArrayBufferView | false {
  // Legacy support: if old flag exists, convert to Float64Array
  if (object.hasOwnProperty(LEGACY_TYPED_ARRAY_LABEL)) {
    return new Float64Array(object.values);
  }

  // New typed array format
  if (object.hasOwnProperty(TYPED_ARRAY_LABEL)) {
    const type = object[TYPED_ARRAY_LABEL] as ArrayBufferType;

    // Look up the constructor from the type
    const ArrayConstructor = TYPE_TO_ARRAY_CONSTRUCTOR[type];

    if (!ArrayConstructor) {
      throw new Error(`Unknown typed array type: ${type}`);
    }

    // If we're reading from JSON and the type is a bigint, convert the strings to BigInts
    return new ArrayConstructor(
      IS_BIGINT_ARRAY[type] ? object.values.map((value: string) => BigInt(value)) : object.values,
    );
  }

  return false;
}
